import type { Editor } from '@tiptap/react';
import type { RichTextPresetStyle } from '@/services/rich-text-presets.api';
import { CALLOUT_DEFAULTS } from './calloutNode';

export const createCalloutAttrs = (style: RichTextPresetStyle) => ({
    iconType: style.boxIconType || CALLOUT_DEFAULTS.iconType,
    icon: style.boxIcon || CALLOUT_DEFAULTS.icon,
    iconEmoji: style.boxIconEmoji || CALLOUT_DEFAULTS.iconEmoji,
    backgroundColor: style.boxBackgroundColor || CALLOUT_DEFAULTS.backgroundColor,
    borderColor: style.boxBorderColor || CALLOUT_DEFAULTS.borderColor,
    textColor: style.boxTextColor || style.color || CALLOUT_DEFAULTS.textColor,
    iconColor: style.boxIconColor || CALLOUT_DEFAULTS.iconColor,
    padding: style.boxPadding || CALLOUT_DEFAULTS.padding,
    fontFamily: style.fontFamily || CALLOUT_DEFAULTS.fontFamily,
    fontSize: style.fontSize || CALLOUT_DEFAULTS.fontSize,
    fontWeight: style.bold ? '700' : CALLOUT_DEFAULTS.fontWeight,
    fontStyle: style.italic ? 'italic' : CALLOUT_DEFAULTS.fontStyle,
    textDecoration: style.underline ? 'underline' : CALLOUT_DEFAULTS.textDecoration,
});

interface PresetStateSetters {
    setTextColor: (value: string) => void;
    setBgColor: (value: string) => void;
}

export function applyPreset(editor: Editor, style: RichTextPresetStyle, setters: PresetStateSetters) {
    const { setTextColor, setBgColor } = setters;

    if (style.boxEnabled) {
        const { from, to } = editor.state.selection;
        const selectedText = editor.state.doc.textBetween(from, to, ' ').trim();
        editor
            .chain()
            .focus()
            .insertContent([
                {
                    type: 'calloutBox',
                    attrs: createCalloutAttrs(style),
                    content: [{ type: 'text', text: selectedText || 'Callout text' }],
                },
                { type: 'paragraph' },
            ])
            .run();

        // Insertion leaves the caret in the trailing paragraph. Move it into the
        // callout instead: that is the text the author just asked for and is
        // about to edit, and it lets the toolbar report the callout's own font
        // rather than the empty paragraph behind it.
        const { doc, selection } = editor.state;
        let insideCallout: number | null = null;
        doc.nodesBetween(0, selection.from, (node, pos) => {
            if (node.type.name === 'calloutBox') insideCallout = pos + node.nodeSize - 1;
        });
        if (insideCallout !== null) editor.commands.setTextSelection(insideCallout);
        return;
    }

    // With nothing selected, the marks below would only apply to text typed
    // next, leaving the paragraph the author is looking at untouched. Cover the
    // caret's block instead, then put the caret back.
    const caret = editor.state.selection.from;
    const collapsed = editor.state.selection.empty;
    if (collapsed) {
        const { $from } = editor.state.selection;
        editor.commands.setTextSelection({ from: $from.start(), to: $from.end() });
    }

    let chain: any = editor.chain().focus();

    if (style.blockType === 'heading1') chain = chain.toggleHeading({ level: 1 });
    if (style.blockType === 'heading2') chain = chain.toggleHeading({ level: 2 });
    if (style.blockType === 'quote') chain = chain.toggleBlockquote();
    if (style.blockType === 'paragraph') chain = chain.setParagraph();

    // The toolbar's font controls read these back off the document, so applying
    // a preset needs no state push to keep them in step.
    const textStyle: Record<string, string> = {};
    if (style.fontSize) textStyle.fontSize = style.fontSize;
    if (style.fontFamily) textStyle.fontFamily = style.fontFamily;
    if (Object.keys(textStyle).length > 0) chain = chain.setMark('textStyle', textStyle);
    if (style.color) {
        setTextColor(style.color);
        chain = chain.setColor(style.color);
    }
    if (style.backgroundColor) {
        setBgColor(style.backgroundColor);
        chain = chain.setHighlight({ color: style.backgroundColor });
    }
    if (style.bold) chain = chain.setBold();
    if (style.italic) chain = chain.setItalic();
    if (style.underline) chain = chain.setUnderline();

    chain.run();

    if (collapsed) editor.commands.setTextSelection(caret);
}
